/**
 * Checkers Game Logic
 * Supports American/English Checkers rules
 */

export const VARIANTS = {
  AMERICAN: {
    name: 'American Checkers',
    boardSize: 8,
    startingRows: 3,
    forcedCapture: true,
    flyingKings: false,
    backwardCapture: true
  },
  INTERNATIONAL: {
    name: 'International Draughts',
    boardSize: 10,
    startingRows: 4,
    forcedCapture: true,
    flyingKings: true,
    backwardCapture: true
  }
};

export class CheckersGame {
  constructor(variant = 'AMERICAN') {
    this.variant = VARIANTS[variant] || VARIANTS.AMERICAN;
    this.boardSize = this.variant.boardSize;
    this.board = [];
    this.currentPlayer = 'red'; // red moves first
    this.selectedPiece = null;
    this.validMoves = [];
    this.captureSequence = false;
    this.gameOver = false;
    this.winner = null;
    this.moveHistory = [];
    
    this.initializeBoard();
  }

  initializeBoard() {
    this.board = Array(this.boardSize).fill(null).map(() => 
      Array(this.boardSize).fill(null)
    );
    
    const startRows = this.variant.startingRows;
    
    // Place black pieces (top)
    for (let row = 0; row < startRows; row++) {
      for (let col = 0; col < this.boardSize; col++) {
        if ((row + col) % 2 === 1) {
          this.board[row][col] = { color: 'black', isKing: false };
        }
      }
    }
    
    // Place red pieces (bottom)
    for (let row = this.boardSize - startRows; row < this.boardSize; row++) {
      for (let col = 0; col < this.boardSize; col++) {
        if ((row + col) % 2 === 1) {
          this.board[row][col] = { color: 'red', isKing: false };
        }
      }
    }
  }

  getPiece(row, col) {
    if (row < 0 || row >= this.boardSize || col < 0 || col >= this.boardSize) {
      return null;
    }
    return this.board[row][col];
  }

  isValidSquare(row, col) {
    return (row + col) % 2 === 1;
  }

  getValidMoves(row, col) {
    const piece = this.getPiece(row, col);
    if (!piece || piece.color !== this.currentPlayer) return [];
    
    const moves = [];
    const captures = [];
    
    // Determine directions based on piece type
    const directions = this.getMoveDirections(piece);
    
    for (const [dRow, dCol] of directions) {
      const newRow = row + dRow;
      const newCol = col + dCol;
      
      // Regular move
      if (this.getPiece(newRow, newCol) === null && this.isValidSquare(newRow, newCol)) {
        if (newRow >= 0 && newRow < this.boardSize && newCol >= 0 && newCol < this.boardSize) {
          moves.push({ row: newRow, col: newCol, isCapture: false });
        }
      }
      
      // Capture move
      const jumpRow = row + dRow * 2;
      const jumpCol = col + dCol * 2;
      const targetPiece = this.getPiece(newRow, newCol);
      
      if (
        targetPiece && 
        targetPiece.color !== piece.color &&
        jumpRow >= 0 && jumpRow < this.boardSize &&
        jumpCol >= 0 && jumpCol < this.boardSize &&
        this.getPiece(jumpRow, jumpCol) === null
      ) {
        captures.push({ 
          row: jumpRow, 
          col: jumpCol, 
          isCapture: true,
          capturedRow: newRow,
          capturedCol: newCol
        });
      }
    }
    
    // Flying kings can move multiple squares
    if (piece.isKing && this.variant.flyingKings) {
      // Add flying king logic here for international variant
    }
    
    // If forced capture is enabled and captures are available, only return captures
    if (this.variant.forcedCapture && captures.length > 0) {
      return captures;
    }
    
    return [...moves, ...captures];
  }

  getMoveDirections(piece) {
    if (piece.isKing) {
      return [[-1, -1], [-1, 1], [1, -1], [1, 1]];
    }
    
    // Regular pieces: red moves up (negative row), black moves down (positive row)
    if (piece.color === 'red') {
      return this.variant.backwardCapture && this.captureSequence
        ? [[-1, -1], [-1, 1], [1, -1], [1, 1]]
        : [[-1, -1], [-1, 1]];
    } else {
      return this.variant.backwardCapture && this.captureSequence
        ? [[-1, -1], [-1, 1], [1, -1], [1, 1]]
        : [[1, -1], [1, 1]];
    }
  }

  getAllValidMoves() {
    const allMoves = [];
    let hasCaptures = false;
    
    for (let row = 0; row < this.boardSize; row++) {
      for (let col = 0; col < this.boardSize; col++) {
        const piece = this.getPiece(row, col);
        if (piece && piece.color === this.currentPlayer) {
          const moves = this.getValidMoves(row, col);
          for (const move of moves) {
            if (move.isCapture) hasCaptures = true;
            allMoves.push({ from: { row, col }, to: move });
          }
        }
      }
    }
    
    // If forced capture, filter to only captures
    if (this.variant.forcedCapture && hasCaptures) {
      return allMoves.filter(m => m.to.isCapture);
    }
    
    return allMoves;
  }

  makeMove(fromRow, fromCol, toRow, toCol) {
    const piece = this.getPiece(fromRow, fromCol);
    if (!piece) return false;
    
    const validMoves = this.getValidMoves(fromRow, fromCol);
    const move = validMoves.find(m => m.row === toRow && m.col === toCol);
    
    if (!move) return false;
    
    // Execute move
    this.board[toRow][toCol] = piece;
    this.board[fromRow][fromCol] = null;
    
    // Handle capture
    if (move.isCapture) {
      this.board[move.capturedRow][move.capturedCol] = null;
    }
    
    // Check for king promotion
    if (!piece.isKing) {
      if (piece.color === 'red' && toRow === 0) {
        this.board[toRow][toCol].isKing = true;
      } else if (piece.color === 'black' && toRow === this.boardSize - 1) {
        this.board[toRow][toCol].isKing = true;
      }
    }
    
    // Record move
    this.moveHistory.push({
      from: { row: fromRow, col: fromCol },
      to: { row: toRow, col: toCol },
      captured: move.isCapture ? { row: move.capturedRow, col: move.capturedCol } : null,
      player: this.currentPlayer
    });
    
    // Check for multi-capture
    if (move.isCapture) {
      const additionalCaptures = this.getValidMoves(toRow, toCol).filter(m => m.isCapture);
      if (additionalCaptures.length > 0) {
        this.captureSequence = true;
        this.selectedPiece = { row: toRow, col: toCol };
        return { continueCapture: true, piece: { row: toRow, col: toCol } };
      }
    }
    
    this.captureSequence = false;
    this.switchPlayer();
    this.checkGameOver();
    
    return { continueCapture: false };
  }

  switchPlayer() {
    this.currentPlayer = this.currentPlayer === 'red' ? 'black' : 'red';
  }

  checkGameOver() {
    const moves = this.getAllValidMoves();
    
    if (moves.length === 0) {
      this.gameOver = true;
      this.winner = this.currentPlayer === 'red' ? 'black' : 'red';
      return true;
    }
    
    // Count pieces
    let redPieces = 0;
    let blackPieces = 0;
    
    for (let row = 0; row < this.boardSize; row++) {
      for (let col = 0; col < this.boardSize; col++) {
        const piece = this.getPiece(row, col);
        if (piece) {
          if (piece.color === 'red') redPieces++;
          else blackPieces++;
        }
      }
    }
    
    if (redPieces === 0) {
      this.gameOver = true;
      this.winner = 'black';
    } else if (blackPieces === 0) {
      this.gameOver = true;
      this.winner = 'red';
    }
    
    return this.gameOver;
  }

  getState() {
    return {
      board: JSON.parse(JSON.stringify(this.board)),
      currentPlayer: this.currentPlayer,
      gameOver: this.gameOver,
      winner: this.winner,
      moveHistory: [...this.moveHistory],
      captureSequence: this.captureSequence,
      variant: this.variant.name
    };
  }

  loadState(state) {
    this.board = JSON.parse(JSON.stringify(state.board));
    this.currentPlayer = state.currentPlayer;
    this.gameOver = state.gameOver;
    this.winner = state.winner;
    this.moveHistory = [...state.moveHistory];
    this.captureSequence = state.captureSequence || false;
  }

  clone() {
    const cloned = new CheckersGame();
    cloned.variant = this.variant;
    cloned.boardSize = this.boardSize;
    cloned.loadState(this.getState());
    return cloned;
  }
}

export default CheckersGame;
